/* eslint-disable */
import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/project-management-service/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300000, // 5 minute timeout for large uploads
  maxContentLength: 100 * 1024 * 1024, // 100MB max
  maxBodyLength: 100 * 1024 * 1024, // 100MB max
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Authentication APIs
export const authAPI = {
  register: (userData) => {
    const { userType, user_type, ...sanitizedUserData } = userData || {};
    return api.post('/v1/auth/register', sanitizedUserData);
  },
  login: (credentials) => api.post('/v1/auth/login', credentials),
  getProfile: () => api.get('/v1/auth/profile'),
  updateProfile: (userData) => api.put('/v1/auth/profile', userData),
  getAdminDashboard: () => api.get('/v1/auth/admin/dashboard'),

  // Forgot Password Flow
  forgotPassword: (email) => api.post('/v1/auth/forgot-password', { email }),
  verifyOTP: (email, otp) => api.post('/v1/auth/verify-otp', { email, otp }),
  resetPassword: (resetToken, newPassword) => api.post('/v1/auth/reset-password', { resetToken, newPassword }),
};

export const userManagementAPI = {
  getUsers: (showDeleted = false) => api.post('/v1/auth/users', { showDeleted }),
  updateUser: (userId, userData) => api.post('/v1/auth/users/update', { userId, ...userData }),
  activateUser: (userId) => api.post('/v1/auth/users/activate', { userId }),
  deactivateUser: (userId) => api.post('/v1/auth/users/deactivate', { userId }),
  deleteUser: (userId) => api.post('/v1/auth/users/delete', { userId }),
};

// Property APIs
export const propertyAPI = {
  getAllProperties: (filters = {}) => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
        params.append(key, filters[key]);
      }
    });
    return api.get(`/v1/properties?${params.toString()}`);
  },

  getFeaturedProperties: (limit = 6) =>
    api.get(`/v1/properties/featured?limit=${limit}`),

  getPropertyById: (id) =>
    api.get(`/v1/properties/${id}`),

  searchProperties: (searchTerm, filters = {}) => {
    const params = new URLSearchParams({ q: searchTerm });
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        params.append(key, filters[key]);
      }
    });
    return api.get(`/v1/properties/search?${params.toString()}`);
  },

  getDistricts: () => api.get('/v1/properties/districts'),

  getTaluks: (district) => api.get(`/v1/properties/districts/${encodeURIComponent(district)}/taluks`),

  createProperty: (propertyData) =>
    api.post('/v1/properties', propertyData),

  updateProperty: (propertyId, propertyData) =>
    api.put(`/v1/properties/${propertyId}`, propertyData),

  deletePropertyImage: (fileId) =>
    api.post('/v1/property/delete-image', { fileId }),

  deleteProperty: (propertyId) =>
    api.delete(`/v1/properties/${propertyId}`),

  getMyProperties: (filters = {}) => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach((key) => {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
        params.append(key, filters[key]);
      }
    });
    const query = params.toString();
    return api.get(`/v1/properties/my-properties${query ? `?${query}` : ''}`);
  },
};

// Property Draft APIs
export const propertyDraftAPI = {
  saveDraft: (draftData, currentStep, sessionId = null, draftId = null) =>
    api.post('/v1/property-drafts/save', { draftData, currentStep, sessionId, draft_id: draftId }),

  getDraft: (sessionId = null) =>
    api.get('/v1/property-drafts', { params: { sessionId } }),

  getDraftById: (draftId) =>
    api.get(`/v1/property-drafts/${draftId}`),

  deleteDraft: (sessionId = null) =>
    api.delete('/v1/property-drafts', { params: { sessionId } }),

  deleteDraftById: (draftId) =>
    api.delete(`/v1/property-drafts/${draftId}`),
};

// Master Data APIs
export const masterDataAPI = {
  getAllMasterData: () =>
    api.get('/v1/master-data/all'),

  fetchStatus: () =>
    api.get('/v1/master-data/fetchStatus'),

  getPropertyTypes: () =>
    api.get('/v1/master-data/property-types'),

  getRoadTypes: () =>
    api.get('/v1/master-data/road-types'),

  getWaterSources: () =>
    api.get('/v1/master-data/water-sources'),

  getCRZTypes: () =>
    api.get('/v1/master-data/crz-types'),

  getPriceRanges: () =>
    api.get('/v1/master-data/price-ranges'),

  getAvailabilityStatuses: () =>
    api.get('/v1/master-data/availability-statuses'),

  getUserTypes: () =>
    api.get('/v1/master-data/user-types'),

  getSewageTypes: () =>
    api.get('/v1/master-data/sewage-types'),

  getDistricts: () =>
    api.get('/v1/master-data/districts'),

  getDistrictsWithTaluks: () =>
    api.get('/v1/master-data/districts-with-taluks'),

  getTaluksForDistrict: (district) =>
    api.get(`/v1/master-data/districts/${encodeURIComponent(district)}/taluks`),
};

// Saved Properties APIs
export const savedPropertyAPI = {
  saveProperty: (propertyId) =>
    api.post('/v1/saved-properties', { propertyId }),

  unsaveProperty: (propertyId) =>
    api.delete(`/v1/saved-properties/${propertyId}`),

  getSavedProperties: () =>
    api.get('/v1/saved-properties'),

  checkIfSaved: (propertyId) =>
    api.get(`/v1/saved-properties/check/${propertyId}`),

  getSavedPropertyIds: () =>
    api.get('/v1/saved-properties/ids'),

  getSavedCount: () =>
    api.get('/v1/saved-properties/count'),
};

// Legal Documents APIs
export const legalDocumentAPI = {
  uploadDocuments: (files, documentType, propertyId = null) => {
    const formData = new FormData();

    // Append all files to FormData
    files.forEach(file => {
      formData.append('documents', file);
    });

    // Append document type
    formData.append('documentType', documentType);
    
    // Append property ID if provided
    if (propertyId) {
      formData.append('propertyId', propertyId);
    }

    // Create a custom axios instance with multipart/form-data headers
    return api.post('/v1/legal-documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  deleteDocument: (filename, driveFileId) => {
    const queryParams = new URLSearchParams();
    if (filename) queryParams.set('filename', filename);
    if (driveFileId) queryParams.set('driveFileId', driveFileId);
    const query = queryParams.toString();
    return api.post(
      `/v1/legal-documents/${encodeURIComponent(filename)}/delete?${query}`,
      { filename, driveFileId }
    );
  },
  
  // Returns a URL string (unauthenticated – use fetchDocument for auth-required endpoints)
  getDocument: (filename) => {
    const baseURL = `${process.env.REACT_APP_API_URL}/project-management-service/api`;
    return `${baseURL}/v1/legal-documents?filename=${encodeURIComponent(filename)}`;
  },

    // Authenticated blob fetch via the api axios instance (includes Bearer token)
  fetchDocument: (filename) =>
    api.get(`/v1/legal-documents?filename=${encodeURIComponent(filename)}`, {
      responseType: 'blob',
    }),

  cleanupOrphanedDocuments: () =>
    api.post('/legal-documents/admin/cleanup'),
};

export default api;
